import * as React from 'react';
import PropTypes from 'prop-types';

import { DropdownField, TextField, FormInput } from '../static/Inputs';
import {Button} from '../static/Elements';
import Icon from '../static/Icons';
import RichTextEditor from 'react-rte';
import CurrencyInput from 'react-currency-input';
import DatePicker from 'react-datepicker/es';
import AsyncSelect from 'react-select/async';

import 'react-datepicker/dist/react-datepicker.css';
import 'draft-js-static-toolbar-plugin/lib/plugin.css';
import createLogger from '../../utils/createLogger';
import {createDidError, createIsLoading} from "../../data/requests/requestsSelectors";
import {companySelector} from "../../data/jobs/jobSelectors";
import {connect} from "react-redux";
import {
  getJobDetails,
  getPostings,
  promoteJob,
  getCompanyRecents,
} from "../../data/jobs/jobActions";
import {createJobSelector} from "../../data/jobs/jobSelectors";
import {createOpenWebJobSelector} from "../../data/owp/owpSelectors";
import ReactLoading from "react-loading";

const DEBUG = true;
const debug = createLogger(DEBUG, `PromotionForm.js`);


const mapStateToProps = (state, ownProps) => {
  const didError = createDidError(PromotionForm.dependencies);
  const jobSelector = createJobSelector(ownProps.id);
  const owpJobSelector = createOpenWebJobSelector(ownProps.id);

  const companyId = ownProps.companyId;

  return {
    isAuthenticated: !!state.auth.session,
    didError: didError(state),
    job: jobSelector(state) || owpJobSelector(state),
    company: companySelector(state, companyId),
    companiesByLetter: state.jobs.companiesByLetter,
  }
};

const mapDispatchToProps = {
  get: getJobDetails,
  promoteJob,
  getPostings,
  getCompanyRecents,
};

class PromotionForm extends React.Component {

  static dependencies = [
    `GET_JOB`,
    `PUT_JOB`,
    'POST_JOB',
    'GET_COMPANY_RECENTS',
  ];

  static propTypes = {
    get: PropTypes.func.isRequired,
    didError: PropTypes.bool,
    isLoading: PropTypes.bool,
    closeModal: PropTypes.func,
  };

  constructor(props) {
    super(props);
    debug.enter('constructor');
    debug.log('job from props:', props.job);
    this.state = {
      editorValue: props.job ?
        RichTextEditor.createValueFromString(props.job.description, 'html')
        : RichTextEditor.createValueFromString('', 'html'),
      contacts: [],
      jobData: props.job ? this.emptyJobData() : null,
      localCompanyList: props.companies.slice(0, 100),
    };
  }

  static getDerivedStateFromProps(props, state) {
    let nextState = {...state};
    if (props.company) {
      nextState.jobData.job_company = {inputValue: props.company.companyname, value: props.company.companyid};
      if (!!props.company.careers_page) {
        nextState.jobData.careers_page = props.company.careers_page;
      }
      return nextState;
    }
    return null;
  }

  emptyJobData = () => {
    if (!this.props.job) {
      return null
    } else {
      return {
        title: this.props.job.title || '',
        agency: this.props.job.agency || '',
        job_company: !!this.props.company ? this.props.company.companyid : null,
        lead_date: new Date(),
        employment_type: this.props.job.employment_type || 'Direct Hire',
        min_salary: this.props.job.min_salary || null,
        max_salary: this.props.job.max_salary || null,
        min_experience: this.props.job.min_experience || null,
        duration: this.props.job.duration || null,
        hourly_pay: this.props.job.hourly_pay || null,
        match_url: this.props.matchUrl || '',
        match_strategy: this.props.matchStrategy || null,
        careers_page: '',
        contact_id: '',
      }
    }
  };

  companyLoadOptions = (inputValue, callback) => {
    const { companiesByLetter } = this.props;

    debug.log(`Entered companyLoadOptions <${inputValue}>`);

    if(!inputValue || inputValue === '') {
      inputValue = 'a';
    }
    /*
     * Trying to make the drop down fast.  Regex'ing across 30k+ companies is too slow -- it's around 5-10 seconds
     * to render the first list of companies, and if you hit the down arrow without a character it's worse.
     *
     * Doing startsWith (so the company name has to start with the letter you type) was way faster but misses some
     * things -- you can't type "realty" and get all companies with realty in their name.
     *
     * So what we do as a compromise is we break teh company name up into words, and the we create a list
     * of companies who has words that start with a, b, c, d... -- if it starts with a non-letter, we stick it in a
     * seperate bucket.  So the state.jobs.companiesByLetter has 27 "buckets" -- one for each lowercase letter and
     * the "_" bucket for everything else.  If a company has a word in it's name that starts with that letter, we
     * use the bucket to search for the company.  We end up with most buckets being a few thousand (with a few being
     * as long as 8k like 'c' and 's')  We could probably take it further (second letter, or third) but this is
     * probably good enough for now.
     */
    inputValue = inputValue.toLowerCase();
    let clist;
    if(companiesByLetter.hasOwnProperty(inputValue[0])) {
      clist = companiesByLetter[inputValue[0]];
    } else {
      clist = companiesByLetter['_'];
    }
    // If it's only one character, no need to search the list, we already have the answer.
    if(inputValue.length === 1) callback(clist);
    let splitName = inputValue.split(' ');

    if (splitName.length === 1) {
      callback(clist.filter (company => company.words.filter (word => word.startsWith (splitName[0])).length > 0));
    } else {
      let new_clist = clist;
      for (let i=0; i <= splitName.length -1; i++) {
        new_clist = new_clist.filter (company => {
          let matchingWords = company.words.filter (word => {
            return word.startsWith (splitName[i])
          });
          return matchingWords.length > 0;
        });
      }
      callback(new_clist)
    }
  };

  onInputChange = (field) => (input) => {
    debug.log(`setting ${field} to ${input}`, input);
    this.setState({jobData: {...this.state.jobData, [field]: input}});
  };

  onTextChange = (value) => {
    this.setState({editorValue: value});
  };

  onRadioChange = (e) => {
    debug.log(`setting contact_id to ${e.target.value}`);
    this.setState({jobData: {...this.state.jobData, contact_id: e.target.value}});
  };

  onChangeCompany = (company) => {
    const { setCompany } = this.props;
    setCompany(company.companyid);
  };

  promote = (e) => {
    e.preventDefault();
    const {job, selectNext} = this.props;
    console.log(job);
    const {closeModal, promoteJob} = this.props;
    let config = this.state.jobData;
    let description_html = this.state.editorValue.toString('html');
    config['description_html'] = this.styleDescription(description_html);
    config['status'] = 'found';
    selectNext();
    promoteJob(job.id, config);
    closeModal();
  };

  duplicateCheck = () => {
    const {duplicateCheck, getCompanyRecents, companyId} = this.props;
    getCompanyRecents(companyId)
      .then(duplicateCheck)
  };

  styleDescription = (description) => {
    let opening_div = `<div style='font-family: Arial, Helvetica, sans-serif; font-size: 14px;'>`;
    let closing_div = `</div>`;
    let new_description = opening_div + description + closing_div;
    debug.log(new_description);
    return new_description
  };

  onCancel = () => {
    this.emptyJobData();
    this.props.closeModal();
  };

  render () {
    const {company, addCompany, editCompany, isLoading} = this.props;
    const contacts = company ? company.contacts : null;
    const {editorValue} = this.state;
    const {
      title,
      agency,
      lead_date,
      employment_type,
      min_salary,
      max_salary,
      min_experience,
      duration,
      hourly_pay,
      careers_page,
      match_url,
      contact_id,
      job_company,
    } = this.state.jobData;

    const {
      onInputChange,
      promote,
      onTextChange,
      onChangeCompany,
      onRadioChange,
      onCancel,
      companyLoadOptions,
      duplicateCheck,
    } = this;

    const titleFieldConfig = {
      type: `text`,
      value: title,
      label: 'Job Title',
      placeholder: 'Sr. Developer',
      onChange: onInputChange(`title`),
    };

    const agencyFieldConfig = {
      type: `text`,
      value: agency,
      label: 'Agency',
      placeholder: 'Kore1',
      onChange: onInputChange(`agency`),
    };

    const companyAsyncFieldConfig = {
      isSearchable: true,
      cacheOptions: true,
      loadOptions: companyLoadOptions,
      onChange: onChangeCompany,
      getOptionValue: (company) => company.companyid,
      getOptionLabel: (company) => company.companyname,
      defaultOptions: true,
      value: company ? { companyname: company.companyname, companyid: company.companyid } : null,
    };

    const datePickerConfig = {
      selected: lead_date,
      name: 'lead_date',
      label: 'Agency Leads Date',
      onChange: onInputChange('lead_date'),
      className: `date-picker-field`,
      todayButton: 'Today',
    };

    const employmentTypeConfig = {
      type: 'text',
      value: employment_type,
      label: "Type",
      onChange: onInputChange(`employment_type`),
      defaultValue: this.props.job.employment_type || 'Direct Hire',
    };

    const matchUrlConfig = {
      type: 'text',
      label: 'Match URL',
      onChange: onInputChange('match_url'),
      value: match_url,
    };

    const careersPageConfig = {
      type: 'text',
      label: 'Company Careers Page URL',
      onChange: onInputChange('careers_page'),
      value: careers_page,
    };

    const toolbarConfig = {
      display: ['INLINE_STYLE_BUTTONS', 'BLOCK_TYPE_BUTTONS', 'BLOCK_TYPE_DROPDOWN', 'HISTORY_BUTTONS'],
      INLINE_STYLE_BUTTONS: [
        {label: 'Bold', style: 'BOLD', className: 'rte-toolbar'},
        {label: 'Italic', style: 'ITALIC'},
        {label: 'Underline', style: 'UNDERLINE'}
      ],
      BLOCK_TYPE_DROPDOWN: [
        {label: 'Normal', style: 'unstyled'},
        {label: 'Heading Large', style: 'header-one'},
        {label: 'Heading Medium', style: 'header-two'},
        {label: 'Heading Small', style: 'header-three'}
      ],
      BLOCK_TYPE_BUTTONS: [
        {label: 'UL', style: 'unordered-list-item'},
        {label: 'OL', style: 'ordered-list-item'}
      ]
    };

    const reactSelectStyles = base => ({ ...base, zIndex: 999 });

    return (
      <FormInput className='promotion-form-container'>

        <div className='text-area-container editorStyles.editor' onClick={this.focus}>
          <RichTextEditor value={editorValue}
                          onChange={onTextChange}
                          toolbarConfig={toolbarConfig}
                          className='rte-textarea'/>
        </div>

        <div className='promotion-form-fields'>

          <div className='company-select'>
            <div className='z-index-top company-select-field'>
              <label>Company </label>
              {
                company ? <Button text onClick={duplicateCheck}>Check for duplicates</Button> : null
              }
              <AsyncSelect {...companyAsyncFieldConfig} styles={reactSelectStyles}/>
            </div>

            {
              isLoading ? (
                <div className="small-loader">
                  <ReactLoading type={'bubbles'}
                                color={'#6b6c6f'}/>
                </div>
              ) : (
                <React.Fragment>
                  <Button onClick={addCompany}><Icon.Add/></Button>
                  <Button onClick={editCompany} disabled={!company}><Icon.Edit/></Button>
                </React.Fragment>
              )
            }


          </div>

          <div className='full-line-field'>
            <TextField {...titleFieldConfig} />
          </div>

          <div className='split-line-field'>
            <TextField {...agencyFieldConfig} />
            <DropdownField {...employmentTypeConfig}>
              <option value='Direct Hire' className={'dropdown-item'}>Direct Hire</option>
              <option value='Contract' className={"dropdown-item"}>Contract</option>
              <option value='Contract To Direct Hire' className={"dropdown-item"}>Contract to Direct Hire</option>
              <option value='Contract, Direct Hire, Contract To Direct Hire' className={"dropdown-item"}>All
              </option>
            </DropdownField>
          </div>

          <div className='split-line-field'>
            <div>
              <label>Lead Date</label>
              <DatePicker {...datePickerConfig} />
            </div>
            <div>
              <label>Min Experience</label>
              <CurrencyInput value={min_experience}
                             onChangeEvent={(e, fv, v) => {
                               this.setState ({jobData: {...this.state.jobData, min_experience: v}})
                             }}
                             suffix=' years' precision='0'/>
            </div>
          </div>

          {
            employment_type !== 'Direct Hire'
              ? (
                <div className='split-line-field'>
                  <div>
                    <label>Duration</label>
                    <CurrencyInput value={duration} precision='0' suffix=' months'
                                   onChangeEvent={(e, fv, v) => {
                                     this.setState ({jobData: {...this.state.jobData, duration: v}})
                                   }}/>
                  </div>
                  <div>
                    <label>Hourly Pay</label>
                    <CurrencyInput value={hourly_pay}
                                   onChangeEvent={(e, fv, v) => {
                                     this.setState ({jobData: {...this.state.jobData, hourly_pay: v}})
                                   }}
                                   prefix='$' precision='0' suffix='/hr'/>
                  </div>
                </div>
              )
              : null
          }

          <div className='split-line-field'>
            <div>
              <label>Min Salary</label>
              <CurrencyInput value={min_salary}
                             onChangeEvent={(e, fv, v) => {
                               this.setState ({jobData: {...this.state.jobData, min_salary: v}})
                             }}
                             prefix='$' precision='0'/>
            </div>
            <div>
              <label>Max Salary</label>
              <CurrencyInput value={max_salary}
                             onChangeEvent={(e, fv, v) => {
                               this.setState ({jobData: {...this.state.jobData, max_salary: v}})
                             }}
                             prefix='$' precision='0'/>
            </div>
          </div>

          <div className='full-line-field'>
            <TextField {...matchUrlConfig} />
          </div>

          <div className='full-line-field'>
            <TextField {...careersPageConfig} />
          </div>

          {
            isLoading ? (
                <article className="contacts-selector-loader">
                  <ReactLoading type={'bubbles'}
                                color={'#6b6c6f'}/>
                </article>
              ) : contacts ? (
              <div className='contacts-selector-container'>
                <h5>Contacts</h5>
                <div/>
                <div className='contact-list'>
                  <div>
                    {
                      contacts.map (contact => (
                        <ContactListItem key={contact['contactid']}
                                         contact={contact} selectedContact={contact_id}
                                         onChange={onRadioChange}/>
                      ))
                    }
                  </div>
                </div>
              </div>
            ) : null
          }
          <div className='form-footer'>
            <Button key='3' danger onClick={onCancel}>Cancel</Button>
            <Button key='4' primary onClick={promote}>Promote</Button>
          </div>
        </div>
      </FormInput>
    )
  }
}

const ContactListItem = ({contact, selectedContact, onChange}) => {
  let name;
  if (contact['contactname'] === null) {
    name = contact['firstname'] + ' ' + contact['lastname'];
  } else {
    name = contact['contactname']
  }
  return (
    <div className='contact-list-item'>
      <input type='radio' name='contact' value={contact['contactid']}
             checked={parseInt(selectedContact, 10) === contact['contactid']} onChange={onChange}/>
      <div className='contact-list-item-box'>
        <div className='contact-list-item-text'>
          <p className='no-margin'>
            {contact['position']} ({name})</p>
          <a href="#" onClick={()=>window.open(contact['linkedinaddress'])}>{contact['linkedinaddress'] || ''}</a>
        </div>
        <div/>
      </div>
    </div>
  )
};

export default connect(mapStateToProps, mapDispatchToProps)(PromotionForm);


// WEBPACK FOOTER //
// ./src/components/AgencyPostings/PromotionForm.js